import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { observer } from "mobx-react";

import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useStore from "../../../../../helpers/useStore";
import {
  CreateDoctorClinicDto,
  InsurerDto,
  NoteDto,
  PayerDto,
  RxDto,
  RxDtoApprovalStatusEnum,
  RxDtoProcessingStatusEnum,
} from "../../../../../api-client";
import Controls from "../../../../../atoms/Controls";
import FlatCard from "../../../../../atoms/FlatCard";
import WarningDialog from "../../../../../atoms/WarningDialog";
import { DoctorContext } from "../../../../../stores/Doctor/doctor.provider";
import {
  useMasterTables,
  getTarifGroupIds,
  getTherapyId,
} from "../../../../../stores/MasterTables";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { confirmDoctorClinicJoin } from "../../../../DoctorManagement/confirmDoctorClinicJoin";
import { confirmDoctorClinicMismatch } from "../../../../DoctorManagement/confirmDoctorClinicMismatch";
import { PatientsContext } from "../../../../../stores/Patients/patients.provider";
import { RxStoreContext } from "../../../../../stores/Rx/rx.provider";
import DoctorDialog from "../../../../PatientDataEntryForm/Doctor";

import classes from "./BG.module.css";
import ValidationCard, { formType } from "../../../../../molecules/ValidationCard";
import ValidationCardRxInfoPage from "../../../../../molecules/ValidationCardRxInfoPage";
import Notes from "../../../../ScheduleOverview/Notes/Notes";
import { NotesContext } from "../../../../../stores/Notes/notes.provider";
import { UserContext } from "../../../../../stores/User/User.provider";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import { validateFrequency, validateTherapy } from "../../../../../molecules/Editable";
import { rxuseForm } from "../../../../PatientDataEntryForm/Form";
import { Delete } from "@mui/icons-material";
import moment from "moment";
import {
  ClinicForModal,
  Doctor,
} from "../../../../DoctorManagement/DoctorTabContent/doctor-client-types";
import VerordnungDialog from "../Kasse/VerordnungDialog";
import DoctorCard from "../../../../../molecules/DoctorCard";
import DoctorClinicAutocomplete from "../DoctorClinicAutocomplete";
import SimpleDocumentViewer from "../../../../Documents/SimpleDocumentViewer";
import { usePatient } from "../../../../../stores/Patients/patient.queries";
import { useRx } from "../../../../../stores/Rx/rx.queries";
import { useSaveRxs } from "../../../../../stores/Rx/rx.mutations";
import { EntryType } from "../../../../PatientDataEntryForm/Receipt";
import { formTherapyFromLine } from "../../../../../helpers/therapyRxLabel";
import { RxFormRow, rowNamesTerm, rowTermText } from "../../../../../helpers/rxFormRow";
import TherapyTermField from "../../../../../molecules/TherapyTermField";
import { usePrescribedTherapyOptions } from "../../../../../stores/MasterTables/masterTables.queries";

interface BGProps {
  selectedRxId?: string;
  patientId?: number;
  handleNewFormData?: (data: RxDto) => void;
  newFormData?: any;
  values?: any; //required only entryType is newRx
  readOnlyView?: boolean;
  openRx?: boolean;
  onCloseRx?: any;
  entryType?: EntryType | undefined;
  updateRxProcessingStatus?: (event: any) => void;
}

const ScalableDiv = styled("div")(({ theme }) => ({
  transformOrigin: "top left",
  [theme.breakpoints.down("sm")]: {
    transform: "scale(0.6)",
  },
  [theme.breakpoints.between("sm", "xl")]: {
    transform: "scale(0.65)",
  },
  [theme.breakpoints.up("xl")]: {
    transform: "scale(0.85)",
  },
}));

const ScalableComponentSimple = ({ children }: any) => {
  return <ScalableDiv>{children}</ScalableDiv>;
};

const BG: React.FC<BGProps> = observer(
  ({
    selectedRxId,
    patientId,
    handleNewFormData,
    newFormData,
    values,
    readOnlyView,
    openRx,
    onCloseRx,
    entryType,
    updateRxProcessingStatus,
  }) => {
    // const { selectedRow, patientValues, handleData, masterTablesData, readOnlyView } = props;
    const { data: masterTablesData } = useMasterTables();
    // Prescribed TERMS — the catalog level a prescription names (MD-232).
    const { data: termOptions = [] } = usePrescribedTherapyOptions();
    // Fetch RX data using the rxId from store
    const { data: rxData, isLoading: isRxLoading, error: rxError } = useRx(selectedRxId!);
    const selectedRow = entryType && newFormData ? newFormData : rxData;

    // Get patient data from store
    const { data: patientData } = usePatient(patientId!);
    const patientValues = entryType === EntryType.newRx && values ? values : patientData;
    const { mutate: patientSaveRxs } = useSaveRxs();
    // TODO: Replace getICD with appropriate hook-based solution
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [unsavedChangesWarning, setShowunsavedChangesWarning] = useState(false);

    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [insuranceOptions, setInuranceOptions] = useState<InsurerDto[]>([]);
    // const [isDoctorNumberDisabled, setIsDoctorNumberDisabled] = useState(true);
    const [doesClinicExist, setDoesClinicExist] = useState(false);
    const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
    const [isClinicModalEditMode, setIsClinicModalEditMode] = useState(false);
    const [currentClinicId, setCurrentClinicId] = useState("");
    const [creationResponse, setCreationResponse] = useState<any>({
      id: "",
      clinics: [],
      contactInfo: {},
      doctorId: "",
      clinicId: "",
    });
    const [currentDoctorId, setCurrentDoctorId] = useState("");
    const [doesDoctorExist, setDoesDoctorExist] = useState(false);

    // Position und DocumentViewer State-Variablen
    const [position, setPosition] = useState<"default" | "right-third">("default");
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

    const tarifGroupsMasterData = getTarifGroupIds(masterTablesData || []);
    //For payer
    const insurer = (masterTablesData || [])
      .filter((obj: any) => "insurer" in obj)
      .map((obj: any) => obj.insurer)[0];

    const filteredInsurer = () => {
      if (!insurer) return;

      const insurerOptions = insurer.filter(
        (option: any) => option.tarifGroup?.id === formValues.tarifGroupId
      );

      setInuranceOptions(insurerOptions);
    };

    const [selectedPayer, setSelectedPayer] = useState<InsurerDto | null>(null);
    const [selectedChipCard, setSelectedChipCard] = useState<string | null>(null);
    const diagnoseGroups = (masterTablesData || [])
      .filter((obj: any) => "diagnosisGroups" in obj)
      .map((obj: any) => obj.diagnosisGroups);

    const rxType = (masterTablesData || [])
      .filter((obj: any) => "rxType" in obj) // Filter objects that have the insuranceStatus property
      .map((obj: any) => obj.rxType);
    //  therapyOptions= therapyOptions[0].filter(
    //       (therapyObj: any) =>

    //         therapyObj.duration
    //     )

    const [checkboxState, setCheckboxState] = useState({
      stabilitätsgrad: false,
      limitierungen: false,
      opTechnik: false,
      wunden: false,
      erreger: false,
      sonstige: false,
      rücksprache: false,
    });

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setCheckboxState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    };

    //For Doctor
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const DoctorStore = useStore(DoctorContext);
    const { getClinicById, getDoctorById, createDoctorWithClinic } = DoctorStore;
    const [isSelectActive, setIsSelectActive] = useState(false);
    const [isAddEditDialogOpen, setAddEditDialogOpen] = useState(false);
    const handleUnsavedChangesWarning = () => {
      handleSave();
    };
    const handleCloseRxDialog = () => {
      // console.log("shjsakjsajj", unsavedChanges);
      if (unsavedChanges) {
        // If there are unsaved changes, set a state to open the warning dialog
        setShowunsavedChangesWarning(true);
      } else {
        onCloseRx();
      }
    };
    const handleCloseUnsavedChangesWarning = () => {
      onCloseRx();
      // If user cancels and chooses not to continue, close the warning dialog
      setShowunsavedChangesWarning(false);
    };

    const account = (masterTablesData || [])
      .filter((obj: any) => "account" in obj)
      .map((obj: any) => obj.account);

    const initialrxValues = {
      tarifGroupId: "BG",
      rxtype: "Berufsgenossenschaft",
      Receiptpayer: "",
      signatureStatus: "",
      isPerpetual: false,
      accountId: "",
      practiceNumber: "",
      doctorNumber: "",
      doctorId: "",
      clinicId: "",
      date: "",
      dob: "",
      isCopaymentExempted: true,
      isAccident: false,
      isBVG: false,
      icdCodeEntries: [{ userText: "" }, { userText: "" }],
      diagnosisText: "",
      diagnosegroup: null,
      hasIndividualSymptoms: false,
      therapyRxs: Array.from({ length: 4 }, (_, index) => ({
        id: uuidv4(),
        amount: "",
        frequency: { id: "", text: "" },
        therapy: { id: "", HMP4: "", price: "", abbreviation: "", description: "" },
        // MD-232: the prescribed term as typed. The server resolves it
        // text -> alias -> term; declared here so the line type carries it.
        rawText: null as string | null,
        isDoubled: false,
        rxSeries: { label: "" },
      })),
      isHomeVisit: selectedRow?.isHomeVisit ?? null,
      therapyReport: false,
      urgent: false,
      symptoms: "",
      therapyFrequency: "",
      amount: "",
      additionalNotes: "",
      duplicate: false,
      accidentDay: "",
      isLongTermRx: false,
      additionalNotes2: "",
      expectedTherapyLength: "",
      isPerpetualApproved: false,
      diagnosisText2: "",
      therapyStart: null,
      approvalStatus: RxDtoApprovalStatusEnum.Fehlt,
      processingStatus: RxDtoProcessingStatusEnum.InBehandlung,
      clinic: {} as any,
    };

    const [formValues, setFormValues] = useState(initialrxValues);
    const [icdCodeEntries, setIcdCodeEntries] = useState(initialrxValues.icdCodeEntries);
    const [filteredTherapies, setFilteredTherapies] = useState([]);
    const [currentClinic, setCurrentClinic] = useState<ClinicForModal | undefined>();
    const [showClinicData, setShowClinicData] = useState(false);
    const [currentDoctor, setCurrentDoctor] = useState<Doctor | ClinicForModal>();
    const [autocompleteSearchTerm, setAutocompleteSearchTerm] = useState("");
    const UserStore = useStore(UserContext);
    const { user } = UserStore;
    const accounts = useUserAccounts();
    const NotesStore = useStore(NotesContext);
    const { getRxNotes, updateNoteById, postNotes, fetchNotes } = NotesStore;
    const [rxNotes, setRxNotes] = useState<NoteDto[]>([]);
    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await getRxNotes(selectedRxId!);

          setRxNotes(response);
        } catch (error) {
          // console.log("error", error);
        }
      };

      fetchData();
    }, [selectedRxId, fetchNotes]);

    useEffect(() => {
      filteredInsurer();
    }, [formValues.tarifGroupId]);

    useEffect(() => {
      setCurrentDoctor(selectedRow?.doctor as any);
      setCurrentClinic(selectedRow?.clinic as any);

      // If embedded relations are missing, fetch by ID from the Rx
      const fetchMissing = async () => {
        if (!selectedRow?.doctor && selectedRow?.doctorNumber) {
          try {
            const doc = await getDoctorById(selectedRow.doctorNumber);
            if (doc) setCurrentDoctor(doc as any);
          } catch (e) { /* ignore */ }
        }
        if (!selectedRow?.clinic && selectedRow?.practiceNumber) {
          try {
            const cli = await getClinicById(selectedRow.practiceNumber);
            if (cli) setCurrentClinic(cli as any);
          } catch (e) { /* ignore */ }
        }
      };
      fetchMissing();
    }, [selectedRow?.doctor, selectedRow?.clinic, selectedRow?.doctorNumber, selectedRow?.practiceNumber]);

    // Keep display fields in sync with the actual doctor/clinic LANR/BSNR
    useEffect(() => {
      if (currentDoctor) {
        const lanr = (currentDoctor as any).LANR || "";
        setFormValues((prev) => ({ ...prev, doctorNumber: lanr, doctorId: currentDoctor.id || prev.doctorId }));
      }
    }, [currentDoctor]);

    useEffect(() => {
      if (currentClinic) {
        const bsnr = (currentClinic as any).BSNR || "";
        setFormValues((prev) => ({ ...prev, practiceNumber: bsnr, clinicId: currentClinic.id || prev.clinicId }));
      }
    }, [currentClinic]);

    useEffect(() => {
      if (creationResponse && creationResponse.doctorId && creationResponse.clinicId) {
        setCurrentDoctor({
          id: creationResponse.doctorId,
          LANR: creationResponse.LANR,
          contactInfo: creationResponse.contactInfo as any,
          clinics: creationResponse.clinics,
        });
        const matchedClinic = (creationResponse.clinics || []).find((clinic: any) => clinic.id === creationResponse.clinicId);
        setCurrentClinic(matchedClinic as any || undefined);
        setFormValues((prev) => ({
          ...prev,
          doctorId: creationResponse.doctorId || "",
          clinicId: creationResponse.clinicId || "",
          doctorNumber: creationResponse.LANR || "",
          practiceNumber: matchedClinic?.BSNR || "",
        }));
      }
    }, [creationResponse]);

    const rxvalidate = (fieldValues = formValues) => {
      let temp: any = { ...rxerrors };
      if ("accountId" in fieldValues) temp.accountId = fieldValues.accountId ? "" : "Pflichtfeld";
      if ("tarifGroupId" in fieldValues)
        temp.tarifGroupId = fieldValues.tarifGroupId ? "" : "Pflichtfeld";

      rxsetErrors({
        ...temp,
      });

      if (fieldValues == formValues) return Object.values(temp).every((x) => x == "");
    };
    const { rxerrors, rxsetErrors } = rxuseForm(formValues, true, rxvalidate);
    const updateNote = async (index: number, updatedCard: NoteDto) => {
      updatedCard.rxId = selectedRow?.id;
      if (updatedCard.id) {
        await updateNoteById(updatedCard.id, user?.id as string, updatedCard);
      } else {
        await postNotes(user?.id as string, updatedCard);
      }
      const updatedNotes = [...rxNotes];
      updatedNotes[index] = updatedCard;
      setRxNotes(updatedNotes);
    };
    const therapyOptions = (masterTablesData || [])
      .filter((obj: any) => "therapy" in obj)
      .map((obj: any) => obj.therapy)[0]
      ?.filter((option: any) => option.tarifGroup.id === formValues.tarifGroupId);

    // Derive therapyType from the selected account (Physio vs. Ergo)
    const accountTherapyType = useMemo(() => {
      if (!formValues.accountId || !accounts) return "Physiotherapie";
      const selectedAccount = accounts.find(
        (a: any) => a.name === formValues.accountId || a.id === formValues.accountId
      );
      return selectedAccount?.therapyType || "Physiotherapie";
    }, [formValues.accountId, accounts]);

    const filterTherapies = () => {
      const filtered = therapyOptions?.filter(
        (option: any) => option.tarifGroup.id === formValues.tarifGroupId &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );
      setFilteredTherapies(filtered);
    };

    useEffect(() => {
      filterTherapies();
    }, [formValues.rxtype, formValues.tarifGroupId, accountTherapyType]);

    // useEffect(() => {
    //   if (selectedRow) {
    //     // Populate the form data with values from selectedRow
    //     setformValues(selectedRow);
    //   } else {
    //     // Use empty values for form data
    //    setformValues(initialrxValues)
    //   }
    // }, [selectedRow]);
    // Initialize with two input boxes

    // Expose the submitForm function to the parent component

    const formattedRxData = (selectedRow: any, initialTherapyRxs: any) => {
      let icdCodeEntries;

      if (selectedRow.icdCodeEntries && selectedRow.icdCodeEntries.length > 0) {
        if (selectedRow.icdCodeEntries.length === 1) {
          icdCodeEntries = [...selectedRow.icdCodeEntries, { userText: "" }];
        } else {
          icdCodeEntries = selectedRow.icdCodeEntries;
        }
      } else {
        icdCodeEntries = [{ userText: "" }, { userText: "" }];
      }
      const updatedformValues = {
        ...selectedRow,
        firstName: patientValues?.firstName || "",
        lastName: patientValues?.lastName || "",
        dob: moment(patientValues?.dob).format("YYYY-MM-DD") || "",
        diagnosisText: selectedRow?.diagnosisText || "",
        diagnosisText2: selectedRow?.diagnosisText2 || "",
        additionalNotes: selectedRow?.additionalNotes || "",
        additionalNotes2: selectedRow?.additionalNotes2 || "",
        symptoms: selectedRow?.symptoms || "",

        insuranceNo: patientValues?.insuranceNo || "",
        // Receiptpayer: selectedRow.payer.insurer || "",
        Receiptpayer:
          insuranceOptions?.find((option: any) => option.id === selectedRow.chipcard)?.name ||
          selectedRow.payer?.name,
        chipcard: selectedRow.chipcard || selectedRow.payer?.ik,
        diagnosegroup:
          diagnoseGroups[0]?.find((option: any) => option.id === selectedRow.diagnosegroup)?.code ||
          selectedRow.diagnosisGroup?.code,
        rxtype:
          rxType[0]?.find((option: any) => option.id === selectedRow.rxtype)?.text ||
          selectedRow.rxtype?.text ||
          "Berufsgenossenschaft",
        accountId:
          account[0]?.find((option: any) => option.id === selectedRow.accountId)?.name ||
          selectedRow.account?.name,
        // status: selectedRow.status || "",
        signatureStatus: selectedRow.signatureStatus || "",
        insuranceStatus: readOnlyView
          ? selectedRow.insuranceStatus
          : selectedRow.insurancestatus || patientValues?.status,
        therapyRxs: initialTherapyRxs,
        tarifGroupId: "BG",
        isCopaymentExempted: true,
        // rxtype:selectedRow.rxtype?.id || "",
        // accountId:selectedRow.account?.id || "",
        // chipcard: selectedRow.payer?.id || "",
        // doctorId: selectedRow.doctor?.contactInfo?.lastName || "",
        bsnr: selectedRow.clinicId || selectedRow.practiceNumber || "",
        // diagnosegroup: selectedRow.diagnosisGroup?.code || "",
        icdCodeEntries: icdCodeEntries,
        // Map new API field names to form's internal keys
        doctorNumber: selectedRow.LANR || "",
        practiceNumber: selectedRow.BSNR || "",
        doctorId: selectedRow.doctorId || selectedRow.doctorNumber || "",
        clinicId: selectedRow.clinicId || selectedRow.practiceNumber || "",
      };
      return updatedformValues;
    };

    const updatedRxValuesToSave = () => {
      const updatedRxValues = {
        ...formValues,
        id: selectedRow?.id || "",
        rxNumber: selectedRow?.rxNumber || "",
        doctorId: formValues.doctorId,
        clinicId: formValues.clinicId,
        LANR: formValues.doctorNumber || "",
        BSNR: formValues.practiceNumber || "",
        accountId:
          account[0].find((option: any) => option.name === formValues.accountId)?.id ||
          formValues.accountId,
        rxtype:
          rxType[0].find((option: any) => option.text === formValues.rxtype)?.id ||
          formValues.rxtype,
        tarifGroupId: "BG",
        diagnosegroup:
          diagnoseGroups[0].find((option: any) => option.code === formValues.diagnosegroup)?.id ||
          formValues.diagnosegroup,
        Receiptpayer:
          insuranceOptions?.find((option: any) => option.name === formValues.Receiptpayer)?.id ||
          formValues.Receiptpayer,

        // therapyRxs:
        //   formValues.therapyRxs
        //     ?.filter((therapy: any) => therapy.therapy.id !== "") // Filter out entries without therapy.therapyId or therapy.id
        //     .map((therapy: any) => ({

        //       frequency: therapy.frequency || "",

        //     })) || [],

        therapyRxs: (formValues.therapyRxs || []) // Ensure formValues.therapyRxs is an array or provide an empty array
          // MD-232: a row is real when it NAMES A TERM. Gating on
          // `therapy.id` dropped every freshly entered row (see rowNamesTerm).
          .filter(rowNamesTerm)
          .map((therapy: any) => {
            if (
              !(therapy.frequency && therapy.frequency.id) ||
              !rowNamesTerm(therapy) ||
              !therapy.amount
            ) {
              throw new Error(
                "Bitte fügen Sie die Behandlung hinzu, um das Rezept zu speichern/bearbeiten (Therapie, Menge, Häufigkeit sind Pflichtfelder) und deaktivieren Sie auch die Zelle nach der Bearbeitung!"
              );
            }
            const validTherapy = validateTherapy(
              rowTermText(therapy),
              masterTablesData || [],
              termOptions
            );
            const validFrequency = validateFrequency(therapy.frequency.id, masterTablesData || []);
            if (!validTherapy || !validFrequency) {
              throw new Error("Bitte geben Sie eine gültige Therapie und Häufigkeit ein.");
            }
            const insurer = (masterTablesData || [])
              .filter((obj: any) => "insurer" in obj)
              .map((obj: any) => obj.insurer); // Extract the insurer array
            // const therapyName = therapy.therapy.abbreviation.toUpperCase();
            const tarifTherapy = getTherapyId(
              masterTablesData || [],
              formValues.tarifGroupId,
              therapy.therapy.abbreviation
            );

            return {
              ...therapy,
              therapy: tarifTherapy
                ? { id: tarifTherapy, ...therapy.therapy }
                : {
                    id: "",
                    abbreviation: "",
                  },
            };
          }),
      };
      return updatedRxValues;
    };

    // const handleCloseRxDialog=()=>{
    //   if(setFormData){
    //     const updatedValues=updatedRxValuesToSave()
    //     setFormData(updatedValues)
    //   }
    //   onCloseRx()
    // }
    const [isHausbesuchSelected, setHausbesuchSelected] = useState<boolean | null>(
      formValues.isHomeVisit
    );
    const handlevisitCheckboxChange = (event: any) => {
      const selectedValue = event.target.value; // "yes" or "no"

      if (selectedValue === "yes") {
        setHausbesuchSelected(true);
        // Set isHomeVisit to true if "yes" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: true,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else if (selectedValue === "no") {
        setHausbesuchSelected(false);
        // Set isHomeVisit to false if "no" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: false,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        setHausbesuchSelected(null); // Set null if neither is selected
        // Set isHomeVisit to null if neither checkbox is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: null,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      }
    };

    useEffect(() => {
      const fetchData = async () => {
        if (selectedRow && selectedRow.rxNumber) {
          // selectedRow = patientValues?.rxs.find((rx: any) => rx.id === selectedRow.id);
          // console.log(
          //   "After checking selectedRow and Patient Value",
          //   selectedRow,
          //   " and",
          //   patientValues.rxs
          // );
          let rxs: [] = [];

          selectedRow.therapyRxs = selectedRow?.therapyRxs?.flatMap((therapy: any): RxFormRow => {
            // Extract therapyRxs from each rx, or default to an empty array
            // Map therapyRxs to the desired format
            // console.log("Therapy Log DATA", therapy, therapy.frequency);
            // console.log("date debug 2: ", selectedRow.date);
            return {
              id: therapy.id || uuidv4(),
              date: selectedRow.date,
              // Carried through the round trip: this is what names the term on
              // save. An API line has `rawText`; fall back to the term id so a
              // line stored before rawText existed still resolves.
              rawText: therapy.rawText ?? therapy.prescribedTherapyId ?? null,
              therapy: formTherapyFromLine(therapy, therapyOptions),
              amount: therapy.amount,
              frequency: therapy.frequency
                ? therapy.frequency.id
                  ? therapy.frequency
                  : { id: therapy?.frequency, text: therapy?.frequency }
                : null,
              // Doubling is `unitsPerDay` since MD-232 — the server no longer
              // sends a persisted `isDoubled`. Carry both: the checkbox reads
              // the boolean, every save channel reads `unitsPerDay`.
              unitsPerDay: therapy.unitsPerDay,
              isDoubled: (therapy.unitsPerDay ?? (therapy.isDoubled ? 2 : 1)) > 1,
              rxSeries: therapy.rxSeries,
            };
          });

          const initialTherapyRxs = Array.from({ length: 4 }, (_, index) => {
            if (index < (selectedRow?.therapyRxs?.length ?? 0)) {
              // If index is within the length of formValues.therapyRxs, use existing data
              return selectedRow?.therapyRxs?.[index];
            } else {
              // Otherwise, add an empty row
              return {
                id: uuidv4(),
                amount: "",
                frequency: { id: "", text: "", prefferedValue: "" },
                therapy: { id: "", HMP4: "", price: "", abbreviation: "", description: "" },
                isDoubled: false,
                // rxSeries:{}
              };
            }
          });
          // Update form data based on fetched data
          const updatedformValues = formattedRxData(selectedRow, initialTherapyRxs);

          // Update form data state
          setFormValues(updatedformValues);

          // Optionally, return the updated form data
          return updatedformValues;
        }
        // Make API request to fetch patient data based on patient ID or any other identifier
        else {
          // console.log("Thshkjaskdkkd", selectedRow);
          const therapyRxs: any =
            selectedRow?.therapyRxs && selectedRow?.therapyRxs.length
              ? Array.from({ length: 4 }, (_, index) => ({
                  ...selectedRow?.therapyRxs?.[index],
                  id: selectedRow?.therapyRxs?.[index]?.id || uuidv4(),
                  amount: selectedRow?.therapyRxs?.[index]?.amount || "",
                  frequency: selectedRow?.therapyRxs?.[index]?.frequency
                    ? selectedRow?.therapyRxs?.[index].frequency
                    : { id: "", text: "" },

                  therapy: selectedRow?.therapyRxs?.[index]?.therapy
                    ? selectedRow?.therapyRxs?.[index].therapy
                    : { id: "", abbreviation: "" },
                }))
              : initialrxValues.therapyRxs;
          const updatedformValues = formattedRxData(
            { ...selectedRow, Receiptpayer: null, chipcard: null },
            therapyRxs
          );

          // Set form values using therapytableData or default to initialrxValues
          setFormValues(updatedformValues);
        }
      };
      fetchData();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRow, patientValues]);
    // console.log("ahsajsjakskska", formValues);
    // const updateRxProcessingStatus = async (event: any) => {
    //   const response = await updateprocessingStatus(selectedRow.id, event?.target.value);
    // };

    const handleOpenAddEditDialog = (lanr: string | null, bsnr: string | null) => {
      setAddEditDialogOpen(true);
    };
    const handleIsPerpetual = () => {
      setFormValues((prevValues: any) => ({
        ...prevValues,
        isPerpetualApproved: true,
      }));
      setOpenModal(false);
    };

    const handleCloseAddEditDialog = (data: any) => {
      // Handle doctor data, LANR, and BSNR passed back from DoctorDialog
      setAddEditDialogOpen(false);

      // Here you can perform further actions with the received data
      if (data && "number" in data) {
        const { number, clinicNumber, contactInfo, id } = data;

        setSelectedDoctor(contactInfo?.lastName);
        setFormValues((prevFormValues: any) => ({
          ...prevFormValues,
          doctorNumber: number,
          practiceNumber: clinicNumber,
        }));

        // For example, you can update doctor form values if needed
        // setDoctorFormValues(doctorData);
      }
    };

    // const [isHausbesuchSelected, setHausbesuchSelected] = useState<boolean | null>(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const handleModalClose = () => {
      setOpenModal(false);
      setFormValues((prevValues: any) => ({
        ...prevValues,
        isPerpetualApproved: false,
      }));
    };

    const handleDeleteTherapy = (therapyId: string) => {
      // Filter out the therapy that matches the given ID
      const updatedTherapies = formValues.therapyRxs.filter((therapy) => therapy.id !== therapyId);

      // Update formData state with new therapy list
      setFormValues({
        ...formValues,
        therapyRxs: updatedTherapies,
      });
    };

    const handleInputChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      index?: any,
      idValue?: string
    ) => {
      // console.log("asisdkkd", event);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { name, value, checked, type, id } = event.target;
      setUnsavedChanges(true);

      const inputValue = type === "checkbox" ? checked : value;

      if (name === "practiceNumber") {
        // No character limit for BG Rxs — allow UUIDs and non-standard IDs
      }

      if (name === "doctorNumber") {
        // No character limit for BG Rxs — allow UUIDs and non-standard IDs
        // Only update the field, do not clear doctor info here!
        setFormValues((prevValues: any) => ({
          ...prevValues,
          doctorNumber: value,
        }));
        return;
      }

      if (name === "date") {
        // console.log("setting date", value);
      }
      if (name === "date" && formValues.isPerpetual && value) {
        setFormValues((prevValues: any) => ({
          ...prevValues,
          unvirtualize: true,
        }));
      }

      const updatedTherapyRxs = [...formValues.therapyRxs];

      if (name === "therapy") {
        // MD-232: the line names the prescribed TERM as the text that was
        // typed, and the server resolves it text -> alias -> term on save.
        // Previously this looked the value up among tarif-bound BILLABLE LINE
        // ITEMS and wrote state only on a hit — so a real term like "KG-ZNS",
        // which is no line item's abbreviation, stored nothing at all and the
        // save reached the server naming no term ("Heilmittel unbekannt").
        // `abbreviation` is kept in step because the table cell reads it.
        if (value) {
          updatedTherapyRxs[index] = {
            ...updatedTherapyRxs[index],
            rawText: value,
            therapy: {
              ...updatedTherapyRxs[index].therapy,
              abbreviation: value,
            },
          };
        } else {
          // Cleared: drop the term as well as the display value.
          updatedTherapyRxs[index] = {
            ...updatedTherapyRxs[index],
            rawText: null,
            therapy: { id: "", HMP4: "", abbreviation: "", description: "", price: "" },
          };
        }
        setFormValues((prevformValues: any) => ({
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      if (name === "amount") {
        updatedTherapyRxs[index] = {
          ...updatedTherapyRxs[index],
          amount: value,
        };

        setFormValues((prevformValues) => ({
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        }));
      }
      if (name === "rxSeries") {
        // Update therapyRxs based on rowIndex and therapyId
        const updatedTherapyRxs = formValues.therapyRxs.map((therapy: any, index: number) => {
          if (index === index && therapy.id === idValue) {
            return {
              ...therapy,
              rxSeries: { id: id, label: value },
            };
          }
          return therapy;
        });

        // Update the formValues state with the updated therapyRxs
        setFormValues((prevformValues) => ({
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        }));
      }
      if (name === "frequency") {
        updatedTherapyRxs[index] = {
          ...updatedTherapyRxs[index],
          frequency: {
            id: value,
            text: value,
          },
        };

        setFormValues((prevformValues) => ({
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        }));
      }
      if (insuranceOptions) {
        if (name === "Receiptpayer") {
          // Autopopulate doctor and bsnr based on LANR selection

          const selectedPayer: any = insuranceOptions?.find(
            (option: any) => option.id === id || option.ik === value
          );
          // console.log("UZZHHHH", selectedPayer);
          if (selectedPayer) {
            setFormValues((prevFormValues: any) => ({
              ...prevFormValues,
              chipcard: selectedPayer?.ik,
              // tarifGroupId: selectedPayer?.tarifGroup?.id,
            }));
            setSelectedChipCard(selectedPayer.ik);
            // console.log("selectedPayer", selectedPayer.name);
          }
        }

        if (name === "chipcard") {
          const selectedPayer: any = insuranceOptions?.find((option: any) => option.ik === value);
          if (selectedPayer) {
            setFormValues((prevFormValues: any) => ({
              ...prevFormValues,
              Receiptpayer: selectedPayer.id,
              // tarifGroupId: selectedPayer?.tarifGroup?.id,
            }));
            setSelectedPayer(selectedPayer);

            // console.log("selectedPayer", selectedPayer.name);
          }
        }
      }

      if (name.startsWith("icdCodeEntries")) {
        const inputElement = document.querySelector(`[name="${name}"]`) as
          | HTMLInputElement
          | HTMLTextAreaElement;
        const cursorPosition = inputElement?.selectionStart || 0;
        let updatedValue = value;
        let newCursorPosition = cursorPosition; // Initialize new cursor position

        // Check if the length is at least 4 and if a period needs to be inserted
        if (value.length >= 4) {
          // Check if the period already exists at the correct position
          if (!value.includes(".")) {
            // Insert a period after the third character if it's not already there
            updatedValue = value.slice(0, 3) + "." + value.slice(3);

            // Adjust cursor position if a period was inserted before the original cursor position
            if (cursorPosition > 3) {
              newCursorPosition = cursorPosition + 1; // Move cursor one position forward
            }
          }
        }

        // Update the form values with the correctly formatted value
        setFormValues((prevValues: any) => ({
          ...prevValues,
          icdCodeEntries: prevValues.icdCodeEntries.map((icdEntry: any, i: number) => {
            return i === index
              ? { id: idValue || undefined, userText: updatedValue }
              : { id: icdEntry?.id, userText: icdEntry?.userText || "" };
          }),
        }));

        // Restore cursor position after the update
        setTimeout(() => {
          if (inputElement) {
            inputElement.selectionStart = inputElement.selectionEnd = newCursorPosition;
          }
        }, 0);
      }

      if (name === "rxtype") {
        setFormValues((prevValues) => ({
          ...prevValues,
          diagnosegroup: null, // Clear diagnosegroup when rxtype changes
        }));
        filterDiagnoseGroups(value);
      } else {
        // console.log("InputValue", inputValue, id);
        // Update other form fields based on their names
        setFormValues((prevValues: any) => ({
          ...prevValues,
          [name]: id ? id : inputValue,
        }));
      }
    };

    const handleSave = async (event?: any) => {
      if (event) {
        setLoading(false);
        event.preventDefault(); // Prevent default form submission behavior
      }
      setUnsavedChanges(false);
      try {
        // console.log("azajjaj", formValues.therapyRxs);
        if (rxvalidate()) {
          // The doctor must actually be registered at the practice the Rx names:
          // the stamp takes its telephone number from the PRACTICE, so a
          // mismatched pair prints a stranger's number on a document that leaves
          // the building. Asked here rather than on a field, because the Arzt-Nr
          // check only fires when that field loses focus - correcting the BSNR
          // afterwards, or re-saving an Rx loaded from the database, slips past it.
          if (!(await confirmDoctorClinicMismatch(currentDoctor, currentClinic))) {
            return;
          }
          setLoading(true);
          const updatedRxValues: any = updatedRxValuesToSave();

          if (currentDoctor) {
            updatedRxValues.doctorId = (currentDoctor as any).id || formValues.doctorId;
            updatedRxValues.LANR = (currentDoctor as any).LANR || formValues.doctorNumber || "";
          } else {
            updatedRxValues.doctor = null;
            updatedRxValues.doctorId = null;
            updatedRxValues.doctorNumber = "";
            updatedRxValues.LANR = "";
          }
          if (currentClinic) {
            updatedRxValues.clinicId = currentClinic.id || formValues.clinicId;
            updatedRxValues.BSNR = (currentClinic as any).BSNR || formValues.practiceNumber || "";
          } else {
            updatedRxValues.clinic = null;
            updatedRxValues.clinicId = null;
            updatedRxValues.practiceNumber = "";
            updatedRxValues.BSNR = "";
          }

          setUnsavedChanges(false);
          setFormValues(updatedRxValues);
          // console.log("UAISKKKD");
          if (entryType === EntryType.newRx && handleNewFormData) {
            handleNewFormData(updatedRxValues);
          } else {
            patientSaveRxs({ patientId: patientId!, rxs: [updatedRxValues] });
            onCloseRx();
            // handleData(updatedformValues);
          }
         

        } else {
          notificationStore.showMessage(
            "Bitte füllen Sie alle erforderlichen Felder aus",
            "warning"
          );
        }
      } catch (error: any) {
        console.error("Error saving Rx:", error);
        notificationStore.showMessage(
          error.message || "Fehler beim Speichern des Rezepts",
          "error",
          error
        );
      } finally {
        // Set loading back to false when the API call is complete
        setLoading(false);
       
      }
    };
    const isAnyCheckboxChecked =
      Object.values(checkboxState).some((checked) => checked) ||
      Boolean(formValues?.additionalNotes);
    const [therapyRxs, setTherapyRxs] = useState(formValues.therapyRxs); // State to manage therapyRxs array
    const componentRef = useRef(null);
    const handleDuplicateRow = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      // console.log("handleDuplicateRow called", { index, value }, event);
      setUnsavedChanges(true);
      setFormValues((prevformValues) => {
        const updatedTherapyRxs = [...prevformValues.therapyRxs];
        const therapy = updatedTherapyRxs[index];

        if (therapy) {
          // No even-amount gate: nothing splits the amount in half any more
          // (that was the old shadow-line model). Odd totals with doubling are
          // legal — e.g. 7 units at 2/day = 3 double days + 1 single.
          const isDuplicate = updatedTherapyRxs.some((othertherapy, otherIndex) => {
            if (otherIndex !== index) {
              // Compare relevant properties
              return therapy.therapy.abbreviation === othertherapy.therapy.abbreviation;
            }
            return false;
          });
          if (isDuplicate) {
            // Show an alert for duplicate therapies
            notificationStore.showMessage(
              "Die Behandlung ist bereits beendet, so dass kein Duplikat zulässig ist.",
              "warning"
            );
            return prevformValues;
          }

          therapy.isDoubled = value as any;
          (therapy as any).unitsPerDay = value ? 2 : 1;
        }

        return {
          ...prevformValues,
          therapyRxs: updatedTherapyRxs,
        };
      });
    };

    const [filteredDiagnoseGroups, setFilteredDiagnoseGroups] = useState([]);

    const filterDiagnoseGroups = (rxtype: any) => {
      const filtered = diagnoseGroups[0].filter(
        (option: any) =>
          option.RxType?.id === rxtype &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );
      setFilteredDiagnoseGroups(filtered);
    };

    const handlePracticeNumberInteraction = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;

      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      // For BG Rxs: allow any non-empty value to trigger lookup (no 9-digit restriction)
      if ((e.type === "blur" || isTriggerKey) && value.length >= 0) {
        try {
          if (value.length > 0) {
            const clinicData = (await getClinicById(value)) as ClinicForModal;
            if (clinicData) {
              setDoesClinicExist(true);
              setCurrentClinic(clinicData);
              setFormValues((prev: any) => ({ ...prev, clinicId: clinicData.id }));
              setShowClinicData(true);
            } else {
              setDoesClinicExist(false);
              setCurrentClinicId(value);
              setCurrentDoctorId(currentDoctor?.id || "");
              setShowClinicData(false);
              setCurrentClinic(undefined);
              setIsClinicModalOpen(true);
            }
          } else if (value.length === 0) {
            setCurrentClinic(undefined);
          }
        } catch (error) {}
      }
    };

    const handleGetDoctorById = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;

      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      // For BG Rxs: allow any non-empty value to trigger lookup (no 9-digit restriction)
      if ((e.type === "blur" || isTriggerKey) && value.length >= 0) {
        try {
          if (value.length > 0) {
            const doctorData: any = await getDoctorById(value);
            if (doctorData) {
              setDoesDoctorExist(true);
              setCurrentDoctor(doctorData);
              setFormValues((prev: any) => ({ ...prev, doctorId: doctorData.id }));

              // Only check association if currentClinic is set and valid
              if (currentClinic && currentClinic.id) {
                const isDoctorInClinic = doctorData.clinics?.some(
                  (clinic: any) => clinic.id === currentClinic.id
                );
                if (isDoctorInClinic) {
                  notificationStore.showMessage(
                    "This doctor exists and is already associated with this clinic",
                    "success"
                  );
                } else {
                  const confirmed = await confirmDoctorClinicJoin(doctorData, currentClinic);
                  if (!confirmed) {
                    // User cancelled — clear the doctor selection (treat as a mistake).
                    setCurrentDoctor(undefined);
                    setDoesDoctorExist(false);
                    setFormValues((prev: any) => ({ ...prev, doctorId: "", doctorNumber: "" }));
                    return;
                  }
                  notificationStore.showMessage(
                    "This doctor exists already and will now get associated with this clinic",
                    "success"
                  );
                  handleAssociateDoctorToClinic(doctorData);
                }
              } else {
                // No clinic entered yet, just set doctor, do not associate
                notificationStore.showMessage(
                  "Bitte geben Sie zuerst eine gültige Betriebsstätten-Nr. (BSNR) ein.",
                  "warning"
                );
              }
            } else {
              setDoesDoctorExist(false);
              setIsClinicModalOpen(true);
              setCurrentDoctorId(value);
              setCurrentClinicId(currentClinic?.id || "");
            }
          } else if (value.length === 0) {
            setCurrentDoctor(undefined);
          }
        } catch (error) {}
      }
    };

    enum ReportMethod {
      Email = "Email",
      Fax = "Fax",
      Post = "Post",
    }

    const handleAssociateDoctorToClinic = async (doctorData: any) => {
      const createDoctorClinicDto = {
        id: doctorData?.id || "",
        contactInfo: {
          salutation: doctorData?.contactInfo?.salutation || "",
          firstNames: doctorData?.contactInfo?.firstNames || "",
          lastName: doctorData?.contactInfo?.lastName || "",
        },
        clinics: [
          {
            id: currentClinic?.id || "",
            contactInfo: {
              businessName: currentClinic?.contactInfo?.businessName || "",
              phone1: currentClinic?.contactInfo?.phone1 || "",
              fax: currentClinic?.contactInfo?.fax || "",
              addressLine1: currentClinic?.contactInfo?.addressLine1 || "",
              addressLine2: currentClinic?.contactInfo?.addressLine2 || "",
              city: currentClinic?.contactInfo?.city || "",
              zip: currentClinic?.contactInfo?.zip || "",
              email: currentClinic?.contactInfo?.email || "",
            },
            notes: currentClinic?.notes || "",
            methodForDrReport: (currentClinic?.methodForDrReport as ReportMethod) || "Email",
            methodForRxOrder: (currentClinic?.methodForRxOrder as ReportMethod) || "Email",
          },
        ],
      };

      // Call createDoctorWithClinic to save data
      // setLoading(true);
      try {
        const response = await createDoctorWithClinic(createDoctorClinicDto);
        return response;
      } catch (error) {
        console.error("Error saving doctor and clinic:", error);
      } finally {
        setLoading(false);
      }
    };

    // useEffect(() => {
    //   const inputValue = selectedBSNR || formValues?.practiceNumber;
    //   const isEmpty = !inputValue || inputValue.trim() === "";

    //   if (isEmpty === false) {
    //     setIsDoctorNumberDisabled(false);
    //   }
    // }, [selectedBSNR, formValues?.practiceNumber, currentClinic?.id]);

    const FormContent = (
      <>
        {/* Left Side */}
        <Grid container spacing={1}>
          {!readOnlyView ? (
            <Grid item xs={12} md={4}>
              <FlatCard title={`Rezept ${selectedRow?.rxNumber || ""}`}>
                <Grid item xs={12}>
                  <Controls.Select
                    id={1}
                    name="accountId"
                    label="Mandant"
                    value={formValues?.accountId}
                    onChange={handleInputChange}
                    options={accounts?.map((option: any) => ({
                      label: option?.name?.toString(),
                      value: option?.name?.toString(),
                      id: option?.id?.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                    required
                    error={rxerrors.accountId}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Checkbox
                    name="isPerpetual"
                    label="Dauerrezept"
                    value={formValues?.isPerpetual}
                    onChange={handleInputChange}
                    disabled={readOnlyView}
                  />
                </Grid>
              </FlatCard>
              <FlatCard title="Arzt">
                <Grid container spacing={2}>
                  {/*  */}
                  <Grid item xs={12}>
                    <DoctorClinicAutocomplete
                      onSelect={(doctor, clinic) => {
                        setCurrentDoctor(doctor as any);
                        setCurrentClinic(clinic as any);
                        setFormValues((prev: any) => ({
                          ...prev,
                          doctorId: doctor.id,
                          clinicId: clinic.id,
                          doctorNumber: doctor.LANR || "",
                          practiceNumber: clinic.BSNR || "",
                        }));
                        setDoesClinicExist(true);
                        setDoesDoctorExist(true);
                        setShowClinicData(true);
                        setUnsavedChanges(true);
                      }}
                      onAddNew={(searchTerm) => {
                        setAutocompleteSearchTerm(searchTerm);
                        setCurrentClinicId("");
                        setCurrentDoctorId("");
                        setIsClinicModalOpen(true);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DoctorCard
                      doctor={currentDoctor}
                      clinic={currentClinic}
                      formData={{
                        doctorNumber: formValues.doctorNumber,
                        clinic: currentClinic,
                      }}
                      showSignatureCheck={false}
                      readOnlyView={readOnlyView}
                      onEdit={() => {
                        setCurrentClinicId(currentClinic?.id || "");
                        setCurrentDoctorId(currentDoctor?.id || "");
                        setIsClinicModalEditMode(true);
                        setIsClinicModalOpen(true);
                      }}
                      onRemoveDoctor={() => {
                        setCurrentDoctor(undefined as any);
                        setFormValues((prev: any) => ({
                          ...prev,
                          doctor: undefined,
                          doctorId: "",
                          doctorNumber: "",
                          LANR: "",
                        }));
                        setUnsavedChanges(true);
                      }}
                      onRemoveClinic={() => {
                        setCurrentClinic(undefined as any);
                        setFormValues((prev: any) => ({
                          ...prev,
                          clinic: undefined,
                          clinicId: "",
                          practiceNumber: "",
                          BSNR: "",
                        }));
                        setUnsavedChanges(true);
                      }}
                      onMigrationComplete={(type, newValue) => {
                        if (type === "doctor") {
                          setFormValues((prev: any) => ({ ...prev, doctorNumber: newValue }));
                          const doctorPK = currentDoctor?.id || (currentDoctor as any)?.id;
                          if (doctorPK) getDoctorById(doctorPK).then((doc) => { if (doc) setCurrentDoctor(doc as any); });
                        } else {
                          setFormValues((prev: any) => ({ ...prev, practiceNumber: newValue }));
                          const clinicPK = currentClinic?.id;
                          if (clinicPK) getClinicById(clinicPK).then((cli) => { if (cli) setCurrentClinic(cli as any); });
                        }
                      }}
                    />
                  </Grid>
                  {/*  */}

                  {!readOnlyView && (
                    <>
                      <Grid item xs={12}>
                        <Controls.RadioGroup
                          label={
                            <Typography sx={{ textAlign: "left" }}>
                              Arztunterschrift vorhanden
                            </Typography>
                          }
                          name="signatureStatus"
                          value={formValues?.signatureStatus}
                          options={[
                            { id: "nicht kontrolliert", label: "nicht kontrolliert" },
                            { id: "vorhanden", label: "vorhanden" },
                            { id: "fehlend", label: "fehlend" },
                          ]}
                          disabled={readOnlyView}
                          onChange={handleInputChange}
                          sx={{
                            width: "100%",
                            paddingLeft: "1rem",
                            textAlign: "left",
                            fontSize: "0.7rem",
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Controls.RadioGroup
                          label={
                            <Typography sx={{ textAlign: "left" }}>Genehmigungsstatus</Typography>
                          }
                          name="approvalStatus"
                          value={formValues?.approvalStatus || RxDtoApprovalStatusEnum.Fehlt}
                          options={Object.values(RxDtoApprovalStatusEnum).map((value) => ({
                            id: value,
                            label: value,
                          }))}
                          disabled={readOnlyView}
                          onChange={handleInputChange}
                          sx={{
                            width: "100%",
                            paddingLeft: "1rem",
                            textAlign: "left",
                            fontSize: "0.7rem",
                          }}
                        />
                      </Grid>
                      <Controls.Checkbox
                        name="isPerpetualApproved"
                        label="Langfristgenehmigung"
                        value={formValues?.isPerpetualApproved}
                        onChange={handleInputChange}
                        disabled={readOnlyView}
                      />
                    </>
                  )}
                  <Grid item xs={12}>
                    <Controls.DatePicker
                      name="date"
                      label="Rezeptdatum"
                      value={formValues?.date}
                      onChange={handleInputChange}
                      formatDate="dd.MM.yy"
                      disabled={readOnlyView}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body1"
                        className={classes.typography}
                        sx={{ marginRight: "8px" }}
                      >
                        Hausbesuch
                      </Typography>
                      <FormControlLabel
                        label=""
                        control={
                          <>
                            <Checkbox
                              checked={formValues.isHomeVisit === true}
                              onChange={handlevisitCheckboxChange}
                              disabled={readOnlyView}
                              name="visitTypeCheckboxJa"
                              value="yes"
                            />
                            <Typography variant="body1">ja</Typography>
                            <Checkbox
                              checked={formValues.isHomeVisit === false}
                              onChange={handlevisitCheckboxChange}
                              disabled={readOnlyView}
                              name="visitTypeCheckboxNein"
                              value="no"
                            />
                            <Typography variant="body1">nein</Typography>
                          </>
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Table
                      sx={{
                        borderCollapse: "collapse",
                        width: "100%",
                        border: "1px solid black",
                        background: "white",
                        fontSize: "10px", // Adjust font size for compactness
                        tableLayout: "auto", // Allows table and cells to adjust width based on content
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          <TableCell>Aktionen</TableCell>
                          <TableCell
                            sx={{
                              borderRight: "1px solid black",
                              padding: "2px",
                              fontSize: "10px",
                              whiteSpace: "nowrap",
                            }}
                            className={classes.typography}
                          >
                            5. Leistungsziffern (Übersicht s. Folgeseite)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: "1px solid black",
                              padding: "2px",
                              fontSize: "10px",
                              whiteSpace: "nowrap",
                            }}
                            className={classes.typography}
                          >
                            Anzahl der Behandlungen insgesamt (max. Behandlungszeit 4 Wochen
                            Ausnahme: Langzeitverordnung s. Nr. 9)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderLeft: "1px solid black",
                              padding: "2px",
                              fontSize: "10px",
                              whiteSpace: "nowrap",
                            }}
                            className={classes.typography}
                          >
                            Behandlungstage pro Woche
                          </TableCell>
                        </TableRow>
                        {formValues?.therapyRxs.map((therapy: any, rowIndex: any) => (
                          <TableRow key={rowIndex}>
                            <TableCell sx={{ padding: "0px", width: "50px", height: "1px" }}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTherapy(therapy.id)}
                                disabled={readOnlyView} // Prevent deletion in read-only mode
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "2px",
                                whiteSpace: "nowrap",
                                borderRight: "1px solid black",
                              }}
                            >
                              <TherapyTermField
                            therapyType={accountTherapyType}
                                name="therapy"
                                id={1}
                                label=""
                                value={therapy.therapy.abbreviation}
                                onChange={(event: any) => handleInputChange(event, rowIndex)}
                                sx={{
                                  fontSize: "10px",
                                  minWidth: "100px", // Minimum width to ensure usability
                                  padding: "0px !important",
                                }}
                                disabled={readOnlyView}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "2px",
                                whiteSpace: "nowrap",
                                borderRight: "1px solid black",
                              }}
                            >
                              <Controls.Input
                                name="amount"
                                label=""
                                value={therapy.amount}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleInputChange(event, rowIndex, therapy.id)
                                }
                                disabled={readOnlyView}
                                sx={{
                                  fontSize: "10px",
                                  minWidth: "60px",
                                  padding: "0px !important",
                                }} // Minimum width to ensure usability
                              />
                            </TableCell>
                            <TableCell sx={{ padding: "2px", whiteSpace: "nowrap" }}>
                              <Controls.Input
                                name="frequency"
                                label=""
                                value={therapy.frequency?.id}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleInputChange(event, rowIndex, therapy.id)
                                }
                                disabled={readOnlyView}
                                sx={{ fontSize: "10px", minWidth: "60px" }} // Minimum width to ensure usability
                              />
                            </TableCell>
                            <TableCell sx={{ padding: "2px", whiteSpace: "nowrap" }}>
                              <Controls.Checkbox
                                name="doubled"
                                value={therapy.isDoubled}
                                label={
                                  <Typography sx={{ fontSize: "8px" }}>Doppelbehandlung</Typography>
                                }
                                disabled={!therapy.therapy.abbreviation || readOnlyView}
                                onChange={(event: any) => handleDuplicateRow(rowIndex, event)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              </FlatCard>
            </Grid>
          ) : (
            <Grid item xs={12} md={3} sx={{ border: "0px dashed green" }}>
              <FlatCard title="Allgemein" sx={{ marginBottom: "2px" }}>
                <Grid container spacing={0} justifyContent="space-between">
                  {/* First Row */}
                  <Grid item xs={5}>
                    <Typography textAlign="left" sx={{ fontSize: "0.8em" }}>
                      Rezept-Typ:
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Controls.Chip
                      label={formValues.rxtype || "-"}
                      sx={{ width: "100%", fontSize: "0.7rem" }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                      Mandant:
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Controls.Chip
                      label={formValues?.accountId || "-"}
                      sx={{ width: "100%", fontSize: "0.7rem", padding: "0px" }}
                    />
                  </Grid>
                  {/* Third Row */}
                  <Grid item xs={5}>
                    <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                      Rx Location:
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Controls.Chip
                      label={
                        formValues.isHomeVisit === true
                          ? "Haus/Heim"
                          : formValues.isHomeVisit === false
                          ? "Praxis"
                          : "-"
                      }
                      sx={{ width: "100%", fontSize: "0.7rem" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controls.Select
                      name="processingStatus"
                      label="Status"
                      onChange={updateRxProcessingStatus}
                      value={formValues?.processingStatus || RxDtoProcessingStatusEnum.InBehandlung}
                      options={Object.values(RxDtoProcessingStatusEnum).map((value) => ({
                        value: value,
                        label: value,
                      }))}
                      noTriggerChange={true}
                    />
                  </Grid>
                </Grid>
              </FlatCard>
              <FlatCard 
                title="Validierung" 
                sxContent={{ 
                  p: 0, 
                  overflow: "hidden",
                  "&:last-child": { 
                    paddingBottom: 1 
                  }
                }}
              >
                <Box 
                  sx={{ 
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    margin: 0,
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "2px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  }}
                >
                  <ValidationCard
                    patientData={patientValues}
                    selectedRx={formValues}
                    form={formType.SHORT}
                  />
                </Box>
              </FlatCard>
              <FlatCard
                title="Rezepthinweise"
                sxContent={{ p: 0.75 }}
                sx={{
                  paddingTop: "5px",
                  marginBottom: "8px",
                  textAlign: "left",
                }}
              >
                <Notes notes={rxNotes} setNotes={updateNote} maxVisibleNotes={3} noteKind="Rezepthinweis" />
              </FlatCard>

              <DoctorCard
                doctor={currentDoctor}
                clinic={currentClinic}
                formData={{
                  doctorNumber: formValues?.doctorNumber,
                  clinic: currentClinic,
                }}
                showSignatureCheck={false}
                readOnlyView={true}
                sx={{ paddingTop: "5px", marginBottom: "8px", textAlign: "left" }}
              />
              
            </Grid>
          )}

          {/* Right Side */}
          <Grid item xs={12} md={5}>
            <ScalableComponentSimple occupiedHeight={readOnlyView ? 0.8 : 1.2}>
              <Container
                sx={{ border: "0px solid yellow" }}
                className={classes.container}
                ref={componentRef}
              >
                <Grid container spacing={1}>
                  {/* Form content */}
                  <Grid item xs={8} sx={{ border: "1px solid black" }}>
                    {/* <Grid container spacing={2}> */}

                    <Grid item xs={12}>
                      <Typography className={classes.typography}>
                        Unfallversicherungsträger
                      </Typography>
                    </Grid>
                    {/* Select Component */}
                    <Grid item xs={12}>
                      <Tooltip
                        title={
                          (selectedPayer?.payer?.address as any)?.city +
                          ", " +
                          (selectedPayer?.payer?.address as any)?.zip
                        }
                      >
                        <Controls.Select
                          disabled={readOnlyView}
                          name="Receiptpayer"
                          label=""
                          id={1}
                          value={formValues.Receiptpayer}
                          onChange={handleInputChange}
                          options={insuranceOptions?.map((option: any) => ({
                            label: `${option.ik} - Payer:${option.payer?.name}  PayerId:${option.payer?.id}   
                             Addresse: ${option.payer?.address.street} ${option.payer?.address.city},${option.payer?.address?.zip}`,
                            value: option.name.toString(),
                            id: option.id.toString(),
                          }))}
                          style={{ width: "100%" }}
                        />
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography className={classes.typography}>
                        Name, Vorname des Versicherten
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <Controls.Input
                            name=""
                            label=""
                            value={patientValues?.lastName}
                            onChange={handleInputChange}
                            style={{ width: "100%", margin: "0px", background: "white" }}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Controls.Input
                            name=""
                            label=""
                            value={patientValues?.firstName}
                            onChange={handleInputChange}
                            style={{ width: "100%", margin: "0px", background: "white" }}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <Typography className={classes.typography}>Geburtsdatum</Typography>
                          <Controls.DatePicker
                            name=""
                            label=""
                            value={formValues?.dob}
                            onChange={handleInputChange}
                            disabled
                            variant="standard"
                            style={{
                              width: "100%",
                              padding: "0px",
                              margin: "0px",
                              fontSize: "10px",
                              "& .MuiInputBase-input": {
                                padding: 0,
                                margin: 0,
                                paddingRight: "0px",
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Divider
                        sx={{
                          flexGrow: 1,
                          //   background: attentionColors.immediateAttention,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {" "}
                      <Typography className={classes.typography}>
                        Vollständige Anschrift
                      </Typography>{" "}
                      <Controls.Input
                        name=""
                        label=""
                        value={`${patientValues?.street || "-"} ${patientValues?.city || ""} ${
                          patientValues?.zip || ""
                        } ${patientValues?.country || ""}`}
                        onChange={handleInputChange}
                        style={{ width: "100%", margin: "0px", background: "white" }}
                        disabled={readOnlyView}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      {" "}
                      <Typography className={classes.typography}>Telefon-Nr.</Typography>{" "}
                      <Controls.Input
                        label=""
                        name=""
                        value={
                          patientValues?.mobile || patientValues?.phone1 || patientValues?.phone2
                        }
                        onChange={handleInputChange}
                        disabled={readOnlyView}
                        // required
                        // error={errors.contact}
                        style={{
                          borderBottom: "2px solid grey",
                          background: "white",
                          // paddingLeft: "5px",
                        }}
                      />
                    </Grid>
                    {/* <Grid item xs={12}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={4}>
                              <Typography>Kostenträgerkennung</Typography>
                              <div style={{ height: "30px" }}>
                                <Controls.Select
                                  //   disabled={readOnlyView}
                                  name="chipcard"
                                  label=""
                                  id={1}
                                  value={selectedChipCard || formValues?.chipcard}
                                  onChange={handleInputChange}
                                  options={insurer[0]?.map((insurer: any) => ({
                                    label: insurer.insurer.toString(),
                                    value: insurer.ik.toString(),
                                  }))}
                                  style={{
                                    width: "100%",
                                    marginTop: "0px",
                                    marginBottom: "0px",
                                    fontSize: "10px",
                                  }}
                                  disabled={readOnlyView}
                                />
                              </div>
                            </Grid>
      
                            <Grid item xs={4}>
                              <Typography>Versicherten-Nr.</Typography>
                              <Controls.Input
                                // disabled={readOnlyView}
                                name="insuranceN"
                                label=""
                                value={formValues?.insuranceNo}
                                onChange={handleInputChange}
                                style={{ width: "100%", margin: "0px" }}
                                disabled={readOnlyView}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <Typography>Status</Typography>
                              <Controls.Select
                                // disabled={readOnlyView}
                                name="insuranceStatus"
                                label=""
                                value={formValues?.insuranceStatus}
                                onChange={handleInputChange}
                                options={[
                                  { label: "10000", value: "10000" },
                                  { label: "30000", value: "30000" },
                                  { label: "50000", value: "50000" },
                                ]}
                                style={{
                                  width: "100%",
                                  marginTop: "0px",
                                  marginBottom: "0px",
                                  fontSize: "10px",
                                }}
                                disabled={readOnlyView}
                              />
                            </Grid>
                          </Grid>
                          <Divider
                            sx={{
                              flexGrow: 1,
                              //   background: attentionColors.immediateAttention,
                            }}
                          />
                        </Grid> */}
                  </Grid>
                  <Grid item xs={4} sx={{ border: "1px solid black" }}>
                    <Grid item xs={12}>
                      <Typography className={classes.typography}>
                        {" "}
                        Sie dürfen Leistungen dieser Verordnung erbringen und abrechnen, wenn Sie
                        die Regelungen der Vereinbarung zwischen den physiotherapeutischen
                        Berufs-verbänden und den Verbänden der UV-Träger - in gültiger Fassuran-
                        anerken-nen.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ paddingTop: "10px" }}>
                      <Divider />
                      <Controls.DatePicker
                        name="accidentDay"
                        label={
                          <Typography className={classes.typography}>
                            Unfalltag und ggf. Aktenzeichen des Unfallversicherungsträgers
                          </Typography>
                        }
                        value={formValues?.accidentDay}
                        onChange={handleInputChange}
                        formatDate="dd.MM.yyyy"
                        disabled={readOnlyView}
                        style={{
                          width: "100%",
                          padding: "0px",
                          margin: "0px",
                          fontSize: "10px",
                          "& .MuiInputBase-input": {
                            padding: 0,
                            margin: 0,
                            paddingRight: "0px",
                          },
                        }}
                      />{" "}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "2px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      1.Diagnose(n), die die Physiotherapie begründen
                    </Typography>
                    <Controls.TextArea
                      name="diagnosisText"
                      rows={4}
                      placeholder="Textdiagnose"
                      value={formValues?.diagnosisText}
                      onChange={handleInputChange}
                      disabled={readOnlyView}
                      style={{ background: "white", width: "100%" }}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "2px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      2. Weitere Diagnose(n) und Kontextfaktoren, die für die Physiotherapie
                      relevant sein können
                    </Typography>
                    <Controls.TextArea
                      name="diagnosisText2"
                      rows={4}
                      placeholder="Textdiagnose2"
                      value={formValues?.diagnosisText2}
                      onChange={handleInputChange}
                      disabled={readOnlyView}
                      style={{ background: "white", width: "100%" }}
                    />
                  </Grid>
                </Grid>
                <Grid container sx={{ borderBottom: "1px solid black" }}>
                  <Typography className={classes.typography}> 3.Therapiehinweise</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={5}>
                      <Grid item xs={12}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "0px !important",
                            marginBottom: "0px !important",
                          }}
                        >
                          <Controls.Checkbox
                            name="stabilitätsgrad"
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  textAlign: "left",
                                  whiteSpace: "nowrap",
                                  maxWidth: "calc(100% - 50px)", // Adjust based on the expected checkbox width
                                }}
                              >
                                Stabilitätsgrad(z.B.belastungsstabil 20kg bis):
                              </Typography>
                            }
                            value={checkboxState.stabilitätsgrad}
                            onChange={handleCheckboxChange}
                            disabled={readOnlyView}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Controls.Checkbox
                            name="limitierungen"
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                  maxWidth: "calc(100% - 50px)", // Adjust based on the expected checkbox width
                                }}
                              >
                                Weitere Limitierungen (z. B. Bewegungsausmaß):
                              </Typography>
                            }
                            value={checkboxState.limitierungen}
                            onChange={handleCheckboxChange}
                            disabled={readOnlyView}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Controls.Checkbox
                            name="opTechnik"
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                  maxWidth: "calc(100% - 50px)", // Adjust based on the expected checkbox width
                                }}
                              >
                                Angewandte OP-Technik:
                              </Typography>
                            }
                            value={checkboxState.opTechnik}
                            onChange={handleCheckboxChange}
                            disabled={readOnlyView}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Controls.Checkbox
                            name="wunden"
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                  maxWidth: "calc(100% - 50px)", // Adjust based on the expected checkbox width
                                }}
                              >
                                Therapieeinschränkende Wunden:
                              </Typography>
                            }
                            value={checkboxState.wunden}
                            onChange={handleCheckboxChange}
                            disabled={readOnlyView}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Controls.Checkbox
                            name="erreger"
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                  maxWidth: "calc(100% - 50px)", // Adjust based on the expected checkbox width
                                }}
                              >
                                Multiresistente Erreger (Typ und Lokalisation):
                              </Typography>
                            }
                            value={checkboxState.erreger}
                            onChange={handleCheckboxChange}
                            disabled={readOnlyView}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Controls.Checkbox
                            name="sonstige"
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                  maxWidth: "calc(100% - 50px)", // Adjust based on the expected checkbox width
                                }}
                              >
                                Sonstige (z. B. orthopädietechnische Versorgung, Sprachbarriere
                                etc.):
                              </Typography>
                            }
                            value={checkboxState.sonstige}
                            onChange={handleCheckboxChange}
                            disabled={readOnlyView}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Controls.Checkbox
                            name="rücksprache"
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                  maxWidth: "calc(100% - 50px)", // Adjust based on the expected checkbox width
                                }}
                              >
                                Bitte um telefonische Rücksprache durch Therapeutin/Therapeuten.
                                (Telefon-Nr. und Zeitfenster angeben):
                              </Typography>
                            }
                            value={checkboxState.rücksprache}
                            onChange={handleCheckboxChange}
                            disabled={readOnlyView}
                          />
                        </div>
                        {/* </Grid> */}
                      </Grid>
                    </Grid>
                    <Grid item xs={6} sx={{ padding: "2px" }}>
                      {isAnyCheckboxChecked && (
                        <Controls.TextArea
                          name="additionalNotes"
                          rows={3}
                          placeholder=""
                          value={formValues?.additionalNotes}
                          onChange={handleInputChange}
                          disabled={readOnlyView}
                          style={{ background: "white", width: "100%" }}
                        />
                      )}{" "}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "2px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      4. Konkrete Therapieziele (z. B. Steigerung ROM, funktioneller Kraftaufbau,
                      Gangschule etc.)
                    </Typography>
                    <Controls.TextArea
                      name="additionalNotes2"
                      rows={3}
                      placeholder=""
                      value={formValues?.additionalNotes2}
                      onChange={handleInputChange}
                      disabled={readOnlyView}
                      style={{ background: "white", width: "100%" }}
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "2px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={12}>
                    <Table
                      sx={{
                        borderCollapse: "collapse",
                        width: "100%",
                        // border: "1px solid red",
                        background: "white",
                        fontSize: "10px", // Adjust font size for compactness
                        tableLayout: "auto", // Allows table and cells to adjust width based on content
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell
                            sx={{
                              borderRight: "1px solid black",
                              padding: "2px",
                              fontSize: "10px",
                              whiteSpace: "nowrap",
                            }}
                            className={classes.typography}
                          >
                            5. Leistungsziffern (Übersicht s. Folgeseite)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: "1px solid black",
                              padding: "2px",
                              fontSize: "10px",
                              whiteSpace: "nowrap",
                            }}
                            className={classes.typography}
                          >
                            Anzahl der Behandlungen insgesamt (max. Behandlungszeit 4 Wochen
                            Ausnahme: Langzeitverordnung s. Nr. 9)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderLeft: "1px solid black",
                              padding: "2px",
                              fontSize: "10px",
                              whiteSpace: "nowrap",
                            }}
                            className={classes.typography}
                          >
                            Behandlungstage pro Woche
                          </TableCell>
                        </TableRow>
                        {formValues?.therapyRxs.map((therapy: any, rowIndex: any) => (
                          <TableRow key={rowIndex}>
                            <TableCell sx={{ padding: "0px", width: "50px", height: "1px" }}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTherapy(therapy.id)}
                                disabled={readOnlyView} // Prevent deletion in read-only mode
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "2px",
                                whiteSpace: "nowrap",
                                borderRight: "1px solid black",
                              }}
                            >
                              <TherapyTermField
                            therapyType={accountTherapyType}
                                name="therapy"
                                id={1}
                                label=""
                                value={therapy.therapy.abbreviation}
                                onChange={(event: any) => handleInputChange(event, rowIndex)}
                                sx={{
                                  fontSize: "10px",
                                  minWidth: "100px", // Minimum width to ensure usability
                                  padding: "0px !important",
                                }}
                                disabled={readOnlyView}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "2px",
                                whiteSpace: "nowrap",
                                borderRight: "1px solid black",
                              }}
                            >
                              <Controls.Input
                                name="amount"
                                label=""
                                value={therapy.amount}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleInputChange(event, rowIndex, therapy.id)
                                }
                                disabled={readOnlyView}
                                sx={{
                                  fontSize: "10px",
                                  minWidth: "60px",
                                  padding: "0px !important",
                                }} // Minimum width to ensure usability
                              />
                            </TableCell>
                            <TableCell sx={{ padding: "2px", whiteSpace: "nowrap" }}>
                              <Controls.Input
                                name="frequency"
                                label=""
                                value={therapy.frequency?.id}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleInputChange(event, rowIndex, therapy.id)
                                }
                                disabled={readOnlyView}
                                sx={{ fontSize: "10px", minWidth: "60px" }} // Minimum width to ensure usability
                              />
                            </TableCell>
                            <TableCell sx={{ padding: "2px", whiteSpace: "nowrap" }}>
                              <Controls.Checkbox
                                name="doubled"
                                value={therapy.isDoubled}
                                label={
                                  <Typography sx={{ fontSize: "8px" }}>Doppelbehandlung</Typography>
                                }
                                disabled={!therapy.therapy.abbreviation || readOnlyView}
                                onChange={(event: any) => handleDuplicateRow(rowIndex, event)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>

                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "10px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={12} sx={{ maxHeight: "300px", overflowY: "auto" }}>
                    <Table
                      sx={{
                        borderCollapse: "collapse",
                        width: "100%",
                        // border: "1px solid red",
                        background: "white",
                      }}
                    >
                      <TableBody sx={{ maxHeight: "200px", overflowY: "scroll" }}>
                        <TableRow>
                          {" "}
                          <TableCell
                            sx={{ borderLeft: "1px solid black" }}
                            className={classes.typography}
                          >
                            6. Leistungszifferen (Übersicht s. Folgeseite)
                          </TableCell>
                          <TableCell sx={{ borderLeft: "1px solid black" }}>
                            Anzahl der Behandlungen insgesamt (max. Behandlungszeit 4 Wochen
                            Ausnahme: Langzeitverordnung s. Nr. 9)
                          </TableCell>
                          <TableCell sx={{ borderLeft: "1px solid black" }}>
                            Behandlungstage pro Woche
                          </TableCell>
                        </TableRow>
                        {/* {formValues?.therapyRxs.map((therapy:any, rowIndex:any) => (
                                      <TableRow
                                        key={rowIndex}
                                        // style={{ border: "1px solid red", height: "1px" }}
                                      >
      
                                        <TableCell
                                          sx={{
                                            padding: "0px",
                                            width: "50px",
                                            height: "1px",
                                          }}
                                        >
                                          <TherapyTermField
                            therapyType={accountTherapyType}
                                          
                                            name="therapy"
                                            id={1}
                                            label=""
                                            value={therapy.therapy.abbreviation }
                                            onChange={(event: any) => handleInputChange(event, rowIndex)}
                                            // options={therapyOptions[0]
                                            //   ?.filter((therapyObj: any) => therapyObj.duration)
                                            //   ?.map((therapy: any) => ({
                                            //     label: therapy?.abbreviation?.toString(),
                                            //     value: therapy?.abbreviation?.toString(),
                                            //   }))}
                                            style={{
                                              width: "250px",
                                              marginTop: "0px",
                                              marginBottom: "0px",
                                              fontSize: "10px",
                                            }}
                                            disabled={readOnlyView}
                                          />
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            padding: "0px",
                                          //   width: "30px",
                                            height: "1px",
                                            borderLeft: "1px solid red",
                                          }}
                                        >
                                          <Controls.Input
                                            name="amount"
                                            
                                            label=""
                                            value={therapy.amount}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                              handleInputChange(event, rowIndex, therapy.id);
                                            }} // Pass rowIndex to handleInputChange
                                            disabled={readOnlyView}
                                           
                                          />
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            padding: "0px",
                                          //   width: "30px",
                                            height: "1px",
                                            borderLeft: "1px solid red",
                                          }}
                                        >
                                        <Controls.Input
                                            name="frequency"
                                            
                                            label=""
                                            value={therapy.frequency?.id}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                              handleInputChange(event, rowIndex, therapy.id);
                                            }} // Pass rowIndex to handleInputChange
                                            disabled={readOnlyView}
                                           
                                          /></TableCell>
                                        <TableCell
                                          sx={{
                                            padding: "0px",
                                            width: "30px",
                                            height: "1px",
                                          }}
                                        >
                                          <Controls.Checkbox
                                            name="doubled"
                                            value={therapy.isDoubled}
                                            label={
                                              <Typography sx={{ fontSize: "10px" }}>
                                                Doppelbehandlung
                                              </Typography>
                                            }
                                            disabled={!therapy.therapy.abbreviation || readOnlyView}
                                            onChange={(event: any) => {
                                              handleDuplicateRow(rowIndex, event);
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            padding: "0px",
                                            width: "30px",
                                            height: "1px",
                                          //   borderLeft: "1px solid red",
                                          }}
                                        >
                                       
                                        </TableCell>
                                        
                                      </TableRow>
                                    ))} */}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "10px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>
                      7.Voraussichtliche Gesamttherapiedauer
                    </Typography>
                    <Controls.TextArea
                      name="expectedTherapyLength"
                      rows={3}
                      placeholder=""
                      value={formValues?.expectedTherapyLength}
                      onChange={handleInputChange}
                      disabled={readOnlyView}
                      style={{ background: "white", width: "100%" }}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "10px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={4}>
                    <Typography className={classes.typography}>
                      8.Physiotherapiebeginn am
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography sx={{ textAlign: "left", fontSize: "12px", whiteSpace: "normal" }}>
                      (spätestens innerhalb von 14 Tagen nach diesem Datum.)
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Controls.DatePicker
                      name="therapyStart"
                      label=""
                      value={formValues?.therapyStart}
                      onChange={handleInputChange}
                      formatDate="dd.MM.yyyy"
                      disabled={readOnlyView}
                      style={{
                        width: "100%",
                        padding: "0px",
                        margin: "0px",
                        fontSize: "10px",
                        "& .MuiInputBase-input": {
                          padding: 0,
                          margin: 0,
                          paddingRight: "0px",
                        },
                      }}
                    />{" "}
                  </Grid>
                  {/* <Grid item xs={12}> */}
                  <Controls.Checkbox
                    name="urgent"
                    label={
                      <Typography
                        sx={{
                          fontSize: "12px",
                          whiteSpace: "normal",
                          textAlign: "left",
                        }}
                      >
                        Bei dringendem Behandlungsbedarf spätestens innerhalb von 7 tagen nach
                        diesem Datum.
                      </Typography>
                    }
                    value={formValues.urgent}
                    onChange={handleInputChange}
                    disabled={readOnlyView}
                  />
                </Grid>
                <Grid container spacing={1} sx={{ borderBottom: "1px solid black" }}>
                  <Grid item xs={12}>
                    <Typography className={classes.typography}>9.</Typography>
                    <Controls.Checkbox
                      name="isLongTermRx"
                      label={
                        <Typography
                          sx={{
                            fontSize: "12px",
                            whiteSpace: "normal",
                            textAlign: "left",
                          }}
                          className={classes.typography}
                        >
                          Langzeitverordnung (bis max. 6 Monate)-Die erforderliche Kostenzusage ist
                          vor Behandlungsbeginn vom
                        </Typography>
                      }
                      value={formValues.isLongTermRx}
                      onChange={(e: any) => {
                        handleInputChange(e);
                        setOpenModal(!openModal);
                      }}
                      disabled={readOnlyView}
                    />
                  </Grid>

                  <Typography sx={{ textAlign: "left", fontSize: "12px", whiteSpace: "normal" }}>
                    Leistungserbringenden oder von der versicherten Person gemäß Rahmenvertrag
                    Physiotherapie einzuholen.
                  </Typography>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  sx={{ paddingTop: "2px", borderBottom: "1px solid black" }}
                >
                  <Grid item xs={3}>
                    <Typography sx={{ textAlign: "left", fontSize: "12px", whiteSpace: "normal" }}>
                      Datum
                    </Typography>
                    <Controls.DatePicker
                      name="date"
                      label=""
                      value={formValues?.date}
                      onChange={handleInputChange}
                      formatDate="dd.MM.yy"
                      disabled={readOnlyView}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <Typography sx={{ textAlign: "left", fontSize: "12px", whiteSpace: "normal" }}>
                      Name, Anschrift und Telefon-Nr. der Durchgangsärztin/des Durchgangsarztes
                    </Typography>
                    <Controls.TextArea
                      name="doctor"
                      rows={3}
                      placeholder=""
                      value={
                        `${currentDoctor?.contactInfo?.salutation || ""} ${
                          currentDoctor?.contactInfo?.firstNames || ""
                        } ${currentDoctor?.contactInfo?.lastName || ""}\n` +
                        `${currentClinic?.contactInfo?.addressLine1 || ""}\n` +
                        `${currentClinic?.contactInfo?.addressLine2 || ""}\n` +
                        `${currentClinic?.contactInfo?.zip || ""}\n` +
                        `Tel: ${currentClinic?.contactInfo?.phone1 || ""}\n` + // Added "Tel:" label
                        `BSNR: ${currentClinic?.BSNR || ""}`
                      }
                      onChange={handleInputChange}
                      disabled
                      style={{ flex: 1, width: "100%", background: "white" }}
                    />
                  </Grid>
                </Grid>
              </Container>
            </ScalableComponentSimple>
          </Grid>
          {!readOnlyView && selectedRow && (
            <Grid item xs={12} md={3} sx={{ border: "0px dotted black" }}>
              
              <FlatCard title="Validierung">
                <ValidationCard
                  patientData={patientValues}
                  selectedRx={
                    // patientValues?.rxs?.filter(
                    //   (rx: any) => rx.rxNumber === selectedRow?.rxNumber
                    // )[0]
                    formValues
                  }
                  form={formType.LONG}
                />
              </FlatCard>
            </Grid>
          )}
        </Grid>
        {openModal && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleModalClose} // Function to close the dialog
            onContinue={handleIsPerpetual} // Function to continue despite unsaved changes
            title="Liegt eine Genehmigung  vor?"
            content={
              <>
                <Typography>
                  {" "}
                  Eine Langzeitgenehmigung ist erforderlich. Wähle die ensprechende
                  Genehmigungsstatus aus.
                </Typography>
                <Controls.RadioGroup
                  label=""
                  name="approvalStatus"
                  value={formValues?.approvalStatus || RxDtoApprovalStatusEnum.Fehlt}
                  options={Object.values(RxDtoApprovalStatusEnum).map((value) => ({
                    id: value,
                    label: value,
                  }))}
                  disabled={readOnlyView}
                  onChange={handleInputChange}
                  sx={{
                    width: "100%",
                    paddingLeft: "1rem",
                    textAlign: "left",
                    fontSize: "0.7rem",
                  }}
                />
              </>
            }
          />
        )}
        {unsavedChangesWarning && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleCloseUnsavedChangesWarning} // Function to close the dialog
            onContinue={handleUnsavedChangesWarning} // Function to continue despite unsaved changes
            title="Änderungen speichern?"
            content="Du hast einige Änderungen gemacht. Möchtest du diese speichern, bevor du schließt?"
            isConfirmSave
          />
        )}
      </>
    );

    // if (!selectedRxId || !entryType) {
    //   return (
    //     <Box p={2}>
    //       <span>Kein Rezept ausgewählt</span>
    //     </Box>
    //   );
    // }

    if (isRxLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (rxError) {
      return (
        <Box p={2}>
          <span>Fehler beim Laden des Rezepts für {selectedRxId}</span>
        </Box>
      );
    }
    return !readOnlyView ? (
      <Dialog
        open={openRx as boolean}
        onClose={handleCloseRxDialog}
        maxWidth="xl"
        fullWidth
        // PaperProps={{
        //   sx: {
        //     position: "fixed",
        //     left: position === "right-third" ? `${window.innerWidth * 0.33}px` : "auto",
        //     top: position === "right-third" ? "50%" : "auto",
        //     transform: position === "right-third" ? "translateY(-50%)" : "none",
        //     width: position === "right-third" ? `${window.innerWidth * 0.67}px` : "auto",
        //     height: position === "right-third" ? "90vh" : "auto",
        //     maxWidth: position === "right-third" ? "none" : "1200px",
        //     margin: position === "right-third" ? "0" : "auto",
        //   },
        // }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <span>BG Dialog</span>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPosition(position === "default" ? "right-third" : "default")}
            sx={{ minWidth: "40px", padding: "4px 8px" }}
          >
            1
          </Button>
        </DialogTitle>
        <DialogContent>{FormContent}</DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseRxDialog} disabled={loading}>
            Abbrechen
          </Button>
          <Button variant="text" onClick={() => setDocumentViewerOpen(!documentViewerOpen)}>
            Dokumentviewer
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Speichern
          </Button>
        </DialogActions>
        <VerordnungDialog
          openAddEditDialog={isClinicModalOpen}
          handleCloseAddEditDialog={() => { setIsClinicModalOpen(false); setIsClinicModalEditMode(false); }}
          clinicId={currentClinicId}
          setCreationResponse={setCreationResponse}
          doctorId={currentDoctorId}
          currentClinic={currentClinic}
          currentDoctor={currentDoctor}
          rxType="Berufsgenossenschaft"
          initialDoctorName={autocompleteSearchTerm}
          isEditMode={isClinicModalEditMode}
          // isDoctorNumberDisabled={isDoctorNumberDisabled}
        />

        {/* SimpleDocumentViewer Komponente */}
        <SimpleDocumentViewer
          open={documentViewerOpen}
          onClose={() => setDocumentViewerOpen(false)}
          position={position}
          patientId={patientValues?.id}
        />
      </Dialog>
    ) : (
      FormContent
    );
  }
);

export default BG;
